import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const SLIDES = [
  [1, "Diapositive 1 — Présentation"],
  [2, "Diapositive 2 — Ressources humaines"],
  [3, "Diapositive 3 — Gestion externalisée"],
  [4, "Diapositive 4 — Tarifs (flyer)"],
  [5, "Diapositive 5 — Comptabilité & paie"],
  [6, "Diapositive 6 — Informatisation"],
  [7, "Diapositive 7 — Fiscalité"],
];

export default function AdminSlidesTexts() {
  const [forms, setForms] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () =>
    api
      .get("/public/content")
      .then((r) => {
        const texts = r.data.texts || {};
        setForms(Object.fromEntries(SLIDES.map(([n]) => [n, {
          tag: texts[`slide_${n}_tag`] || "",
          title: texts[`slide_${n}_title`] || "",
          text: texts[`slide_${n}_text`] || "",
        }])));
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => {
    load();
  }, []);

  const save = async (n) => {
    setBusy(n);
    try {
      await api.patch(`/admin/slides/${n}`, forms[n]);
      toast.success(`Textes de la diapositive ${n} enregistrés.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const reset = async (n) => {
    setBusy(n);
    try {
      await api.delete(`/admin/slides/${n}`);
      toast.success("Textes par défaut rétablis.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const set = (n, k) => (e) => setForms({ ...forms, [n]: { ...forms[n], [k]: e.target.value } });

  if (!forms) {
    return <p className="flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>;
  }

  return (
    <div data-testid="admin-slides-texts-view">
      <h2 className="font-display text-xl font-bold text-korban-navy">Textes des diapositives</h2>
      <p className="mt-2 text-sm text-slate-500">
        Personnalisez les textes du diaporama. Laissez vide pour conserver le texte par défaut. Dans le titre, une
        ligne = une ligne affichée (passez à la ligne pour découper le titre).
      </p>
      <div className="mt-6 space-y-5">
        {SLIDES.map(([n, label]) => {
          const custom = forms[n].tag || forms[n].title || forms[n].text;
          return (
            <div key={n} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`slide-texts-${n}`}>
              <p className="text-sm font-bold text-korban-navy">{label}</p>
              <div className="mt-3 grid gap-3">
                <div>
                  <label htmlFor={`st-tag-${n}`} className="mb-1 block text-xs font-bold text-slate-600">Sur-titre</label>
                  <input id={`st-tag-${n}`} value={forms[n].tag} onChange={set(n, "tag")}
                    placeholder="Ex. Cabinet de consultance" data-testid={`slide-tag-${n}`} className="field field-light" />
                </div>
                <div>
                  <label htmlFor={`st-title-${n}`} className="mb-1 block text-xs font-bold text-slate-600">Titre</label>
                  <textarea id={`st-title-${n}`} rows={2} value={forms[n].title} onChange={set(n, "title")}
                    placeholder="Titre de la diapositive — une ligne par ligne affichée"
                    data-testid={`slide-title-${n}`} className="field field-light resize-none" />
                </div>
                <div>
                  <label htmlFor={`st-text-${n}`} className="mb-1 block text-xs font-bold text-slate-600">Texte descriptif</label>
                  <textarea id={`st-text-${n}`} rows={3} value={forms[n].text} onChange={set(n, "text")}
                    placeholder="Texte affiché sous le titre"
                    data-testid={`slide-text-${n}`} className="field field-light resize-none" />
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => save(n)} disabled={busy === n} data-testid={`slide-save-${n}`}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-korban-navy px-4 py-2 text-xs font-bold text-korban-gold transition-all hover:-translate-y-0.5 hover:bg-korban-navy2 disabled:opacity-50">
                  {busy === n ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Enregistrer
                </button>
                {custom && (
                  <button onClick={() => reset(n)} disabled={busy === n} data-testid={`slide-reset-${n}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white px-4 py-2 text-xs font-bold text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">
                    <RotateCcw size={13} /> Textes par défaut
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
